/* *
 * This `Popout` component is a placeholder, awaiting a robust replacement from
 * Angelou. Any code here should be treated as ephemeral.
 * */

import { forwardRef, useEffect, useRef } from "react";

import { Button, useMergedRefs } from "@newsela/angelou";
import PropTypes from "prop-types";

import { flavors, verticalSnaps, horizontalSnaps } from "./Popout.constants";
import {
  popoutCss,
  underlayCss,
  spotlightCss,
  snapTargetCss,
  cancelButtonCss,
  rowCss,
  spaciousRowCss,
  denseRowCss,
} from "./Popout.style";

const propTypes = {
  children: PropTypes.node,
  isOpen: PropTypes.bool,
  onChange: PropTypes.func,
  flavor: PropTypes.oneOfType([
    PropTypes.oneOf(Object.values(flavors)),
    PropTypes.object,
    PropTypes.array,
  ]),
  isSnapped: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.object,
    PropTypes.array,
  ]),
  verticalSnap: PropTypes.oneOfType([
    PropTypes.oneOf(Object.values(verticalSnaps)),
    PropTypes.object,
    PropTypes.array,
  ]),
  horizontalSnap: PropTypes.oneOfType([
    PropTypes.oneOf(Object.values(horizontalSnaps)),
    PropTypes.object,
    PropTypes.array,
  ]),
  cssFor: PropTypes.shape({
    root: PropTypes.any,
    spotlightShadowUnderlay: PropTypes.any,
  }),
};

const noop = () => null;

const Popout = forwardRef(
  (
    {
      children,
      isOpen,
      onChange = noop,
      flavor = flavors.raised,
      isSnapped = true,
      verticalSnap = verticalSnaps.bottom,
      horizontalSnap = horizontalSnaps.center,
      cssFor = {},
    },
    externalRef,
  ) => {
    const internalRef = useRef();
    const mergedRef = useMergedRefs(internalRef, externalRef);
    useEffect(() => {
      // Trigger popout close on click outside of it.
      if (!isOpen) return;
      const onDocumentClick = (event) => {
        if (!internalRef.current.contains(event.target)) {
          onChange(false);
        }
      };
      document.addEventListener("click", onDocumentClick, true);
      return () => {
        document.removeEventListener("click", onDocumentClick, true);
      };
    }, [isOpen]);
    if (!isOpen) return null;
    return (
      <>
        <div
          ref={mergedRef}
          css={[
            popoutCss({ isSnapped, verticalSnap, horizontalSnap, flavor }),
            cssFor.root,
          ]}
        >
          {children}
        </div>
        <div css={[underlayCss({ flavor }), cssFor.spotlightShadowUnderlay]}>
          <Button
            /* AUTOGENERATED TODO: update angelou to new flavor.
          see https://github.com/newsela/angelou/blob/main/src/components/Button/README.md#MIGRATION
          for migration guide. */
            legacy_flavor={Button.legacy_flavor.incognito}
            __cssFor={{ root: cancelButtonCss }}
          >
            Cancel
          </Button>
        </div>
      </>
    );
  },
);

Popout.propTypes = propTypes;
Popout.flavors = flavors;
Popout.verticalSnaps = verticalSnaps;
Popout.horizontalSnaps = horizontalSnaps;
Popout.spotlightCss = spotlightCss;
Popout.snapTargetCss = snapTargetCss;
Popout.rowCss = rowCss;
Popout.spaciousRowCss = spaciousRowCss;
Popout.denseRowCss = denseRowCss;

export default Popout;
